import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

import OperationItem from './OperationItem';
import { operations } from '../../constants';
import { selectDarkToggle } from '../../store/NavigationBar/selectors';
import '../../variables.scss';
import './styles.scss';
import { sendGAEvent } from '../../store/GoogleAnalytics/actions';
import { changeEmbeddingType } from '../../store/OperationsMenu/actions';
import { selectEmbeddingFullType } from '../../store/OperationsMenu/selectors';
import { FOCUS_OPERATIONS_MENU_SEARCH_BAR } from '../../constants/google-analytics';
import { setIsOpen } from '../../store/ExampleGallery/actions';
import EmbeddingType from '../../utils/embeddingType';

// Component containing operations that embedding sdk has. Each operation item can be dragged into the code editor to display
// the code to execute the operation.

const OperationsMenu = ({
  isDarkTheme,
  embeddingType,
  setIsPopUpOpen,
  onSendGAEvent,
  onChangeType,
}) => {
  // The search term in search bar
  const [searchTerm, setSearchTerm] = useState('');
  const [operationGroups, setOperationGroups] = useState();

  // Add operations in a subcategory under operations menu
  const buildDomFromOps = (operationList, dropdown = false) => {
    return operationList.map((opGroup) => {
      return (
        <details open={dropdown} key={opGroup.groupName}>
          <summary>
            {opGroup.groupName} ({opGroup.operationList.length})
          </summary>
          <ul className="operation-item-list">
            {opGroup.operationList.map((op) => {
              return (
                <OperationItem
                  op={op}
                  key={op.displayName}
                  isDarkTheme={isDarkTheme}
                  groupName={opGroup.groupName}
                />
              );
            })}
          </ul>
        </details>
      );
    });
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  const buildEmbedTypeOperations = (operationList) => {
    const operationGroupsValue = operationList.find(
      (op) => op.embeddingType === embeddingType,
    ).operationGroups;
    setOperationGroups(operationGroupsValue);
    return buildDomFromOps(operationGroupsValue, false);
  };

  const [displayDom, setDisplayDom] = useState('');

  useEffect(() => {
    setDisplayDom(buildEmbedTypeOperations(operations));
  }, [embeddingType]);

  const clickCheck = (type) => {
    if (embeddingType.includes(type)) {
      return 'type-icon-clicked';
    }
    return 'type-icon';
  };

  useEffect(() => {
    if (searchTerm !== '') {
      const filteredOps = [];
      operationGroups.forEach((opGroup) => {
        const newOpGroup = { groupName: opGroup.groupName };
        newOpGroup.operationList = opGroup.operationList.filter((op) =>
          op.displayName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        if (newOpGroup.operationList.length > 0) {
          filteredOps.push(newOpGroup);
        }
      });
      setDisplayDom(buildDomFromOps(filteredOps, true));
    } else {
      setDisplayDom(buildEmbedTypeOperations(operations));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `buildDomFromOps` and `operationsDom` should not be passed into the dependencies array
  }, [searchTerm]);

  return (
    <div className={isDarkTheme ? 'operations-menu dark' : 'operations-menu'}>
      <div className="head">Embedding Template</div>
      <div className="types">
        <button
          type="button"
          className={clickCheck(EmbeddingType.Bot)}
          onClick={() => onChangeType(EmbeddingType.Bot)}
        >
          <img src="./templates/images/Bot.png" alt="Bot" />
          <span className="tooltiptext">Agent</span>
        </button>
        <button
          type="button"
          className={clickCheck(EmbeddingType.Dashboard)}
          onClick={() => onChangeType(EmbeddingType.Dashboard)}
        >
          <img src="./templates/images/Dossier.png" alt="Dossier" />
          <span className="tooltiptext">Dashboard</span>
        </button>
        <button
          type="button"
          className={clickCheck(EmbeddingType.Visualization)}
          onClick={() => onChangeType(EmbeddingType.Visualization)}
        >
          <img src="./templates/images/Viz.png" alt="Viz" />
          <span className="tooltiptext">Visualization</span>
        </button>
        <button
          type="button"
          className={clickCheck(EmbeddingType.Library)}
          onClick={() => onChangeType(EmbeddingType.Library)}
        >
          <img src="./templates/images/Library.png" alt="Library" />
          <span className="tooltiptext">Library</span>
        </button>
      </div>

      <button
        type="button"
        className="example-gallery-trigger"
        onClick={() => setIsPopUpOpen(true)}
      >
        Create From a {EmbeddingType.getFilterTapType(embeddingType)} Template
      </button>

      <div className="head">Operations</div>

      <div className="search-bar">
        <input
          type="search"
          name="Search"
          className="search-input"
          placeholder="Search Operations"
          onChange={(event) => {
            setSearchTerm(event.target.value);
          }}
          onFocus={() => {
            onSendGAEvent(FOCUS_OPERATIONS_MENU_SEARCH_BAR);
          }}
        />
        <span className="material-icons">search</span>
      </div>

      <div className="details-wrapper">{displayDom}</div>
    </div>
  );
};

OperationsMenu.propTypes = {
  isDarkTheme: PropTypes.bool.isRequired,
  onSendGAEvent: PropTypes.func.isRequired,
  onChangeType: PropTypes.func.isRequired,
  embeddingType: PropTypes.string.isRequired,
  setIsPopUpOpen: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    isDarkTheme: selectDarkToggle(state),
    embeddingType: selectEmbeddingFullType(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onSendGAEvent: sendGAEvent,
      onChangeType: changeEmbeddingType,
      setIsPopUpOpen: setIsOpen,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(OperationsMenu);
